<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CreateDMPanelModal from './CreateDMPanelModal.vue';
import {
  getChannelDisplayName,
  getDMPeerMember,
  isDirectMessageChannel,
} from './channelDisplay';
import {
  lastMessagePreview,
  relativeActivityLabel,
  unixFromTimestamp,
} from 'dashboard/routes/dashboard/internalChat/messagePreview';

const emit = defineEmits(['select']);

const store = useStore();
const { t } = useI18n();
const createDMModalRef = ref(null);

const currentUserId = useMapGetter('getCurrentUserID');
const uiFlags = computed(() => store.getters['internalChat/getUIFlags']);

const recentChannels = computed(() => {
  return (store.getters['internalChat/getChannels'] || [])
    .filter(channel => channel.status !== 'archived' && !channel.hidden)
    .sort((a, b) => {
      const tsA = unixFromTimestamp(a.last_activity_at);
      const tsB = unixFromTimestamp(b.last_activity_at);
      if (tsA !== tsB) return tsB - tsA;
      return (b.id || 0) - (a.id || 0);
    });
});

function displayName(channel) {
  return getChannelDisplayName(channel, currentUserId.value);
}

function dmAvatar(channel) {
  const peer = getDMPeerMember(channel, currentUserId.value);
  return peer || (channel.members || [])[0] || null;
}

function previewText(channel) {
  return lastMessagePreview(channel, t);
}

function activityLabel(channel) {
  return relativeActivityLabel(channel.last_activity_at);
}

function channelIcon(channel) {
  if (channel.channel_type === 'private_channel') return 'i-lucide-lock';
  return 'i-lucide-hash';
}

function selectChannel(channelId) {
  emit('select', channelId);
}

function openCreateDM() {
  createDMModalRef.value?.open();
}

function handleDMCreated(channelId) {
  emit('select', channelId);
}

function showUnreadBadge(channel) {
  return !!(channel.unread_count || channel.has_unread_mention);
}

function unreadBadgeLabel(channel) {
  const count = Number(channel.unread_count || 0);
  if (count > 99) return '99+';
  if (count > 0) return String(count);
  return t('INTERNAL_CHAT.MENTION_BADGE');
}

function unreadBadgeIsWide(channel) {
  return unreadBadgeLabel(channel).length > 1;
}
</script>

<template>
  <div class="flex h-full min-h-0 flex-col">
    <div class="flex items-center justify-between px-3 py-2">
      <span class="text-xs font-medium text-n-slate-10">
        {{ t('INTERNAL_CHAT.HOME.TITLE') }}
      </span>
      <Button
        v-tooltip="t('INTERNAL_CHAT.NEW_DM')"
        icon="i-lucide-message-square-plus"
        ghost
        xs
        @click="openCreateDM"
      />
    </div>

    <div
      v-if="uiFlags.isFetching && !recentChannels.length"
      class="flex flex-1 items-center justify-center text-sm text-n-slate-10"
    >
      {{ t('INTERNAL_CHAT.LOADING_MESSAGES') }}
    </div>

    <div v-else class="flex-1 overflow-y-auto px-1.5 pb-2">
      <div
        v-if="!recentChannels.length"
        class="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center"
      >
        <Icon icon="i-lucide-messages-square" class="size-8 text-n-slate-8" />
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('INTERNAL_CHAT.HOME.EMPTY_TITLE') }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{ t('INTERNAL_CHAT.HOME.EMPTY_SUBTITLE') }}
        </p>
      </div>

      <button
        v-for="channel in recentChannels"
        :key="channel.id"
        type="button"
        class="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-n-alpha-2"
        @click="selectChannel(channel.id)"
      >
        <Avatar
          v-if="isDirectMessageChannel(channel)"
          :name="dmAvatar(channel)?.name || displayName(channel)"
          :src="dmAvatar(channel)?.avatar_url || ''"
          :status="dmAvatar(channel)?.availability_status"
          :size="28"
          rounded-full
        />
        <div
          v-else
          class="flex size-7 shrink-0 items-center justify-center rounded-md bg-n-alpha-2 text-n-slate-11"
        >
          <Icon :icon="channelIcon(channel)" class="size-3.5" />
        </div>
        <div class="flex min-w-0 flex-1 flex-col gap-0.5">
          <div class="flex items-center gap-2">
            <span
              class="min-w-0 truncate text-sm font-medium text-n-slate-12"
            >
              {{ displayName(channel) }}
            </span>
            <span
              v-if="activityLabel(channel)"
              class="ms-auto shrink-0 text-[11px] text-n-slate-10"
            >
              {{ activityLabel(channel) }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <span
              class="min-w-0 truncate text-xs"
              :class="
                channel.unread_count > 0 ? 'text-n-slate-12' : 'text-n-slate-10'
              "
            >
              {{ previewText(channel) }}
            </span>
            <span
              v-if="showUnreadBadge(channel)"
              class="ms-auto inline-grid size-4 shrink-0 place-items-center rounded-full bg-n-ruby-9 text-[10px] font-semibold leading-none text-white"
              :class="{ 'w-auto min-w-4 px-1': unreadBadgeIsWide(channel) }"
            >
              {{ unreadBadgeLabel(channel) }}
            </span>
          </div>
        </div>
      </button>
    </div>

    <CreateDMPanelModal ref="createDMModalRef" @created="handleDMCreated" />
  </div>
</template>
